/**
 * Cuándo y cómo avisar que un cliente escribió: las DECISIONES, sin el ruido.
 *
 * El 06-oct-2026 Rubén reportó que los avisos eran imperceptibles: «casi ni se
 * sabe que alguien escribió». Medido en el código, no era una impresión:
 *
 * - el tono eran dos senos de 0,13 s a 0,09–0,16 de volumen, sin armónicos,
 *   pensados para «no molestar» y que en una oficina no se oían;
 * - no había pop-up del sistema: con el portal detrás de otra ventana, lo único
 *   que avisaba era ese tono y el «(N)» de la pestaña;
 * - nadie le decía a la persona que las notificaciones estaban apagadas, ni
 *   dónde se prendían.
 *
 * El modelo es el portal de Factoring (`recordatorio-wsp`), que pasó por lo
 * mismo en setiembre: un botón «Avisos» con sonido y pop-up por separado, sus
 * pruebas, y una invitación discreta cuando falta activar algo. Lo que cambia
 * acá es QUIÉN oye qué: lo decide el rol (Layout), con las mismas marcas que
 * el sonido ya usaba.
 *
 * Esto vive aparte del ruido (`campana.ts`) porque `AudioContext` y
 * `Notification` no existen en Node: separado, todo lo que decide se prueba
 * (`tests/unit/test_los_avisos_se_notan.py`), y es donde están los errores
 * caros —avisar de todo al abrir el portal, prometer un aviso que el navegador
 * no va a mostrar—.
 */
import { telefonoVisible } from './identidad'

// ── Qué cuenta como mensaje nuevo ─────────────────────────────────────────

/** Una conversación con mensaje del cliente, como la manda el contador del
 *  sidebar (`/conversations/pending-count`, `entrantes`). */
export interface Entrante {
  conversation_id: string
  contact_name?: string | null
  phone?: string | null
  contact_phone?: string | null
  celular_declarado?: string | null
  /** Cuándo escribió el cliente por última vez. */
  last_inbound_ts: string
  /** Lo que escribió, ya recortado. Vacío en mensajes anteriores al campo. */
  ultimo_del_cliente?: string | null
  /** Cuántos mensajes mandó en total el cliente. */
  mensajes_cliente?: number | null
  assigned_to?: string | null
}

/** Un entrante nuevo, con cuántos mensajes llegaron desde el sondeo anterior. */
export interface EntranteNuevo extends Entrante {
  nuevos: number
}

/**
 * Las conversaciones donde el cliente escribió algo NUEVO entre dos sondeos.
 *
 * **`antes = null` no avisa nada**, y es la regla que más importa: al abrir el
 * portal llegan de golpe todas, con mensajes de hace horas, y sin esto sonaría
 * un pop-up por cada una. `null` es «todavía no sé qué había»; una lista vacía
 * es «no había nada», y ahí el primero que llega SÍ es nuevo.
 *
 * Compara el instante del último mensaje DEL CLIENTE: cuando contesta el bot o
 * el asesor, la conversación se mueve y el cliente no habló.
 *
 * `nuevos` sale del contador cuando los dos sondeos lo traen; si no —un
 * mensaje anterior al contador—, se sabe que hubo al menos uno.
 */
export function entrantesNuevos(
  antes: ReadonlyArray<Entrante> | null,
  ahora: ReadonlyArray<Entrante>,
): EntranteNuevo[] {
  if (antes === null) return []
  const previo = new Map(antes.map((e) => [e.conversation_id, e]))
  const nuevos: EntranteNuevo[] = []
  for (const e of ahora) {
    if (!e.last_inbound_ts) continue
    const anterior = previo.get(e.conversation_id)
    if (anterior && e.last_inbound_ts <= anterior.last_inbound_ts) continue
    const total = Number(e.mensajes_cliente) || 0
    const antesTotal = Number(anterior?.mensajes_cliente) || 0
    const diferencia = anterior && total > antesTotal ? total - antesTotal : 1
    nuevos.push({ ...e, nuevos: Math.max(1, diferencia) })
  }
  return nuevos
}

// ── Qué dice el pop-up ────────────────────────────────────────────────────

/** Cuánto del mensaje entra en el pop-up. Windows corta el cuerpo en unas tres
 *  líneas; pasado esto el texto se pierde igual, pero a mitad de palabra. */
export const LARGO_DEL_CUERPO = 120

/** Recorta por palabra, y por carácter solo si es una palabra larguísima —un
 *  link—. Cuenta caracteres y no unidades de UTF-16: cortar un emoji a la
 *  mitad deja un rombo con signo de pregunta. */
function recortar(texto: string, largo: number): string {
  const letras = Array.from(texto)
  if (letras.length <= largo) return texto
  const corte = letras.slice(0, largo - 1).join('')
  const espacio = corte.lastIndexOf(' ')
  const hasta = espacio > corte.length * 0.6 ? corte.slice(0, espacio) : corte
  return `${hasta.trimEnd()}…`
}

/**
 * Cómo se nombra al cliente fuera del portal: el nombre si lo hay, si no el
 * teléfono, y si tampoco —un alias de WhatsApp sin número— lo que dice la
 * lista. Nunca «Sin nombre»: dentro de una notificación de Windows, sin
 * contexto, no se entiende.
 */
export function quienEs(e: Entrante): string {
  return (e.contact_name ?? '').trim() || telefonoVisible(e)
}

/** Lo que escribió, en una línea. Los marcadores de adjunto que guarda el bot
 *  («[document] DNI.pdf») se leen como lo que son. */
export function comienzoDelMensaje(e: Entrante): string {
  const texto = (e.ultimo_del_cliente ?? '').replace(/\s+/g, ' ').trim()
  const adjunto = /^\[(document|image|audio|video|sticker)\]\s*(.*)$/i.exec(texto)
  if (adjunto) {
    const tipo = adjunto[1].toLowerCase()
    const nombre = adjunto[2]
    const icono = tipo === 'image' ? '📷 Foto'
      : tipo === 'audio' ? '🎤 Audio'
      : tipo === 'video' ? '🎬 Video'
      : tipo === 'sticker' ? '🙂 Sticker'
      : '📄 Documento'
    return recortar(nombre ? `${icono}: ${nombre}` : icono, LARGO_DEL_CUERPO)
  }
  return recortar(texto, LARGO_DEL_CUERPO)
}

/**
 * El título y el cuerpo del pop-up.
 *
 * Con un cliente: su nombre y cuántos mensajes mandó, y de cuerpo lo que
 * escribió —es lo que permite decidir desde el pop-up si hay que ir ya—. Con
 * varios no hay lugar para los mensajes y se dice quiénes son.
 */
export function textoDelAviso(nuevos: ReadonlyArray<EntranteNuevo>): { titulo: string; cuerpo: string } {
  if (nuevos.length === 1) {
    const e = nuevos[0]
    return {
      titulo: e.nuevos > 1
        ? `💬 ${quienEs(e)} · ${e.nuevos} mensajes nuevos`
        : `💬 ${quienEs(e)} escribió`,
      cuerpo: comienzoDelMensaje(e) || 'Está esperando respuesta',
    }
  }
  const primeros = nuevos.slice(0, 3).map(quienEs).join(', ')
  const resto = nuevos.length - 3
  return {
    titulo: `💬 ${nuevos.length} clientes escribieron`,
    cuerpo: resto > 0 ? `${primeros} y ${resto} más` : primeros,
  }
}

/** A dónde lleva el pop-up. */
export interface DestinoDelAviso {
  /** La ruta del portal: la conversación, o la lista si son varias. */
  url: string
  boton: string
}

/**
 * Con un cliente, su conversación: en la bandeja si es de quien mira —ahí se le
 * contesta—, y en En vivo si es de otro o de nadie, que es lo que supervisión
 * tiene para mirar. Con varios, la lista: abrir el primero sería elegir por la
 * persona cuál atender, y el orden en que llegaron dos mensajes en el mismo
 * sondeo no dice nada.
 */
export function destinoDelAviso(
  nuevos: ReadonlyArray<Entrante>,
  p: { miEmail: string; supervisa: boolean },
): DestinoDelAviso {
  const lista = p.supervisa ? '/live' : '/inbox'
  if (nuevos.length !== 1) return { url: lista, boton: 'Ver conversaciones' }
  const e = nuevos[0]
  const mia = (e.assigned_to ?? '').toLowerCase() === p.miEmail.toLowerCase() && p.miEmail !== ''
  const ruta = mia || !p.supervisa ? '/inbox' : '/live'
  return { url: `${ruta}?c=${encodeURIComponent(e.conversation_id)}`, boton: 'Abrir conversación' }
}

/** El logo del portal, de ícono del pop-up. Windows lo baja por URL absoluta:
 *  el pop-up se dibuja fuera de la página. */
export const ICONO_DEL_AVISO = '/logo-universal.png'

/** Lo que se le pasa a `showNotification`. `renotify` y `actions` no están en
 *  el `lib.dom` de TypeScript —Firefox y Safari no las tienen—, pero Chrome y
 *  Edge sí, y son los navegadores del equipo. */
export interface OpcionesDelPopup {
  body: string
  tag: string
  renotify: boolean
  silent: boolean
  icon: string
  requireInteraction: boolean
  timestamp?: number
  actions: { action: string; title: string }[]
  data: { url: string }
}

/**
 * Cómo se arma el pop-up. Tres opciones que Factoring aprendió en TEST:
 *
 * - **`tag` + `renotify: true`.** El `tag` hace que dos avisos se reemplacen en
 *   vez de apilarse —diez globos son peor que ninguno—, y sin `renotify`
 *   Chrome en Windows suprime el globo de toda notificación que reemplaza a
 *   otra: salía el primero del día y los siguientes entraban mudos al centro
 *   de notificaciones.
 * - **`silent: true`.** El ruido lo hace el portal, con su interruptor. Sin
 *   esto Windows suma su campanita —dos sonidos por mensaje— y suena aunque la
 *   persona haya apagado el sonido para quedarse solo con el pop-up.
 * - **Fijo en pantalla solo si nadie mira el portal**, y con un botón: en
 *   Windows, sin al menos uno, el aviso se va a los cinco segundos aunque pida
 *   quedarse.
 *
 * `timestamp` es cuándo escribió el cliente: si el pop-up queda horas en el
 * centro de notificaciones, lo que importa es cuánto lleva esperando.
 */
export function opcionesDelPopup(p: {
  cuerpo: string
  destino: DestinoDelAviso
  icono: string
  portalALaVista: boolean
  cuando?: string
}): OpcionesDelPopup {
  const ms = p.cuando ? Date.parse(p.cuando) : NaN
  return {
    body: p.cuerpo,
    tag: 'cliente-escribio',
    renotify: true,
    silent: true,
    icon: p.icono,
    requireInteraction: !p.portalALaVista,
    ...(Number.isFinite(ms) ? { timestamp: ms } : {}),
    actions: [{ action: 'abrir', title: p.destino.boton }],
    data: { url: p.destino.url },
  }
}

/**
 * Las mismas opciones sin el botón, para `new Notification`: es el camino de
 * repuesto sin service worker, y ahí `actions` tira `TypeError` en Chrome —los
 * botones son solo de las notificaciones persistentes—.
 */
export function sinBotones(o: OpcionesDelPopup): Omit<OpcionesDelPopup, 'actions'> {
  const { actions, ...resto } = o
  void actions
  return resto
}

/** El aviso de uno o varios clientes, listo para mostrar. */
export function armarAviso(
  nuevos: ReadonlyArray<EntranteNuevo>,
  entorno: { icono: string; portalALaVista: boolean; miEmail: string; supervisa: boolean },
): { titulo: string; opciones: OpcionesDelPopup } {
  const { titulo, cuerpo } = textoDelAviso(nuevos)
  const cuando = nuevos.map((e) => e.last_inbound_ts).sort().pop()
  return {
    titulo,
    opciones: opcionesDelPopup({
      cuerpo,
      destino: destinoDelAviso(nuevos, entorno),
      cuando,
      icono: entorno.icono,
      portalALaVista: entorno.portalALaVista,
    }),
  }
}

/**
 * El de «Probar pop-up». Sale FIJO aunque se dispare con el portal al frente:
 * la prueba tiene que mostrar el aviso como sale cuando nadie lo mira, que es
 * cuando importa. Tocarlo solo trae el portal al frente.
 */
export function avisoDePrueba(icono: string): { titulo: string; opciones: OpcionesDelPopup } {
  return {
    titulo: '💬 Así se ve un aviso',
    opciones: opcionesDelPopup({
      cuerpo: 'Cuando un cliente escribe, acá aparece quién es y el comienzo de su mensaje.',
      destino: { url: '', boton: 'Abrir conversación' },
      icono,
      portalALaVista: false,
    }),
  }
}

/**
 * «Expediente listo» (03-nov-2026): un expediente del asesor quedó completo y
 * espera que lo descargue. Lleva a «Mis expedientes», no a la conversación: lo
 * que hay que hacer es bajar los documentos, y ahí están.
 */
export function avisoDeExpedienteListo(icono: string): { titulo: string; opciones: OpcionesDelPopup } {
  return {
    titulo: 'Expediente listo para descargar',
    opciones: {
      ...opcionesDelPopup({
        cuerpo: 'Un cliente tuyo entregó todos sus documentos. Está en «Mis expedientes».',
        destino: { url: '/mis-expedientes', boton: 'Abrir Mis expedientes' },
        icono,
        portalALaVista: false,
      }),
      // Con su propio `tag`: no reemplaza al aviso de un cliente que escribió.
      tag: 'expediente-listo',
    },
  }
}

/** Lo que el service worker le manda a la pestaña al tocar el pop-up. El
 *  mismo literal está en `public/sw.js`, que no puede importar TypeScript; un
 *  test comprueba que coincidan. */
export const MENSAJE_DEL_SW = 'pgh-abrir-aviso'

/**
 * La ruta que pide abrir un mensaje del service worker, `''` para solo traer
 * el portal al frente, o `null` si el mensaje no es suyo.
 *
 * Valida la forma entera, y que la ruta sea del portal: por el mismo canal
 * puede llegar otra cosa el día que el service worker haga algo más, y un
 * `navigate` a una dirección ajena no puede salir de un pop-up.
 */
export function queAbrir(mensaje: unknown): string | null {
  if (!mensaje || typeof mensaje !== 'object') return null
  const m = mensaje as Record<string, unknown>
  if (m.tipo !== MENSAJE_DEL_SW) return null
  const url = typeof m.url === 'string' ? m.url : ''
  if (url === '') return ''
  return url.startsWith('/') && !url.startsWith('//') ? url : null
}

// ── Lo que dice el panel de avisos ────────────────────────────────────────
//
// El copy vive acá y no en el componente porque es lo único de los avisos que
// se puede probar sin navegador. Y ahí es donde duele equivocarse: decir
// «suena» cuando el navegador todavía no dejó sonar hace que alguien confíe en
// un tono que no va a salir.

/** Lo que el navegador dice del permiso del pop-up. */
export type PermisoPopup = 'concedido' | 'pedible' | 'bloqueado' | 'sin-soporte'

export interface PreferenciasDeAviso {
  /** La persona quiere el tono. */
  sonido: boolean
  /** La persona quiere el pop-up del sistema. */
  popup: boolean
  /** El navegador ya dejó sonar: hubo un gesto y el audio está despierto. */
  audioListo: boolean
  permiso: PermisoPopup
}

/**
 * La línea de abajo de «Sonido». Distingue querer el tono de poder darlo:
 * entre que carga la pantalla y la persona toca algo, el audio está dormido
 * —el navegador exige un gesto— y el aviso NO sale. Decirlo explica por qué el
 * primero del día pudo no oírse.
 */
export function textoDelSonido(p: Pick<PreferenciasDeAviso, 'sonido' | 'audioListo'>): string {
  if (!p.sonido) return 'Apagado: no suena nada'
  if (!p.audioListo) return 'Suena apenas toques algo del portal'
  return 'Suena cada vez que un cliente escribe'
}

/**
 * La línea de abajo de «Pop-up». `bloqueado` es el caso que importa: desde el
 * código NO se puede volver a pedir —`requestPermission()` devuelve `denied`
 * sin mostrar nada— así que el texto dice dónde se desbloquea.
 */
export function textoDelPopup(p: Pick<PreferenciasDeAviso, 'popup' | 'permiso'>): string {
  if (p.permiso === 'sin-soporte') return 'Este navegador no los tiene'
  if (p.permiso === 'bloqueado') return 'Bloqueado: se permite desde el candado de la barra de direcciones'
  if (p.permiso === 'pedible') return 'Hay que permitirlo: el navegador lo va a preguntar'
  if (!p.popup) return 'Apagado: no aparece el aviso de Windows'
  return 'Sale aunque estés en otra ventana, con quién escribió y un botón para abrirlo'
}

/** ¿El interruptor del pop-up se puede tocar? */
export function sePuedeEncenderElPopup(permiso: PermisoPopup): boolean {
  return permiso === 'concedido' || permiso === 'pedible'
}

/** ¿El interruptor del pop-up se ve encendido? Solo con las dos cosas: la
 *  persona lo quiere Y el navegador lo deja. */
export function popupPuesto(p: Pick<PreferenciasDeAviso, 'popup' | 'permiso'>): boolean {
  return p.popup && p.permiso === 'concedido'
}

/**
 * Qué hace el interruptor del pop-up al tocarlo, o `null` si no hace nada.
 *
 * Invierte lo que se VE, no la preferencia guardada: para quien entra por
 * primera vez la preferencia viene encendida y el interruptor se ve apagado,
 * porque todavía no hay permiso. Invertir la preferencia hacía que el primer
 * clic no hiciera nada visible (Factoring, 26-set-2026).
 */
export function alTocarElPopup(
  p: Pick<PreferenciasDeAviso, 'popup' | 'permiso'>,
): { encender: boolean; pedirPermiso: boolean } | null {
  if (!sePuedeEncenderElPopup(p.permiso)) return null
  const encender = !popupPuesto(p)
  return { encender, pedirPermiso: encender && p.permiso === 'pedible' }
}

/**
 * Lo que dice el botón de la cabecera, sin abrirlo: «Avisos» a secas cuando
 * todo está como debe, y lo que falta cuando falta algo. Un botón que dice
 * siempre lo mismo obliga a abrirlo para saber si el portal va a avisar.
 */
export function resumenDeAvisos(p: PreferenciasDeAviso): string {
  if (!p.sonido && !popupPuesto(p)) return 'Avisos apagados'
  if (!p.sonido) return 'Avisos sin sonido'
  if (p.permiso === 'pedible' || (p.permiso === 'concedido' && !p.popup)) return 'Avisos · falta el pop-up'
  return 'Avisos'
}

/** ¿El botón de la cabecera tiene que llamar la atención? Con algo apagado
 *  que se puede prender desde el portal. */
export function faltaActivarAlgo(p: PreferenciasDeAviso): boolean {
  return resumenDeAvisos(p) !== 'Avisos'
}

/** Lo que dice la invitación de la lista cuando falta activar algo. */
export interface InvitacionAvisos {
  titulo: string
  detalle: string
  /** `null` cuando no hay nada que el portal pueda prender: bloqueado en el
   *  navegador y con el sonido ya encendido. */
  boton: string | null
}

/**
 * Si hay que invitar a activar los avisos, y con qué palabras. Es la franja de
 * WhatsApp Web —«Las notificaciones están desactivadas. Activar»—: una línea
 * arriba de la lista, que se cierra, y que dice dónde queda el botón para la
 * próxima vez.
 *
 * `null` cuando no falta nada, y también sin soporte de notificaciones y con
 * el sonido encendido: en ese navegador no hay nada más que activar.
 */
export function invitacionAActivar(p: PreferenciasDeAviso): InvitacionAvisos | null {
  const faltaSonido = !p.sonido
  const donde = 'También desde «Avisos», arriba a la derecha.'

  if (p.permiso === 'bloqueado') {
    return {
      titulo: 'Las notificaciones están bloqueadas en este navegador',
      detalle: 'Para permitirlas: toca el candado a la izquierda de la dirección, '
        + 'activa «Notificaciones» y recarga la página.'
        + (faltaSonido ? ' El sonido sí se puede activar desde acá.' : ''),
      boton: faltaSonido ? 'Activar el sonido' : null,
    }
  }

  const faltaPopup = p.permiso === 'pedible' || (p.permiso === 'concedido' && !p.popup)
  if (!faltaSonido && !faltaPopup) return null

  // Solo se avisa del cuadro de Chrome cuando de verdad va a salir.
  const chrome = p.permiso === 'pedible' ? ' El navegador te va a preguntar: elige «Permitir».' : ''

  if (faltaSonido && faltaPopup) {
    return {
      titulo: 'Los avisos de mensajes están desactivados',
      detalle: `Hoy solo te enteras si estás mirando la lista.${chrome} ${donde}`,
      boton: 'Activar',
    }
  }
  if (faltaPopup) {
    return {
      titulo: 'Las notificaciones están desactivadas',
      detalle: `Actívalas para ver quién escribe aunque estés en otra ventana.${chrome} ${donde}`,
      boton: 'Activar',
    }
  }
  return {
    titulo: 'El sonido de avisos está apagado',
    detalle: `Actívalo para oír cuando un cliente escribe. ${donde}`,
    boton: 'Activar',
  }
}

/**
 * El tooltip del botón: la frase entera, para quien no lo abre. El pop-up solo
 * se promete con el permiso concedido: prometer un aviso que no va a salir es
 * peor que no prometer nada.
 */
export function detalleDeAvisos(p: PreferenciasDeAviso): string {
  const partes: string[] = []
  if (p.sonido) partes.push('suena')
  if (popupPuesto(p)) partes.push('avisa en pantalla')
  if (partes.length === 0) {
    return 'No suena ni avisa cuando un cliente escribe. El número de la pestaña sigue.'
  }
  const como = partes.join(' y ')
  const frase = `${como[0].toUpperCase()}${como.slice(1)} cuando un cliente escribe.`
  return p.sonido && !p.audioListo
    ? `${frase} El sonido arranca apenas toques algo del portal.`
    : frase
}
